import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SettingsPage } from '../components/SettingsPage';
import { HoursAside } from '../components/SettingsAsides';
import { useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { PageHeader, SavedNote, Section, Switch } from '../components/forms';
import { Button, ErrorNote, Field } from '../components/ui';
import { WeekHoursEditor, type WeekHours } from '../components/WeekHoursEditor';
import { api, ApiError } from '../lib/api';
import { useLocale, useT } from '../lib/i18n';
import { LocationSwitcher } from '../components/LocationSwitcher';
import { useCurrentLocation, useLocations } from '../lib/location';

type Holiday = { code: string; date: string; name: { nl: string; en: string }; closed: boolean };
type Closure = { id: string; dateFrom: string; dateUntil: string; message: string | null };

/** The location being viewed (chosen in the location switcher, or the only one). */
export const useFirstLocation = useCurrentLocation;

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : e ? fallback : null);

export function HoursPage() {
  const t = useT();
  const locale = useLocale();
  const { orgId } = useParams({ from: '/s/$orgId/salon/openingstijden' });
  const locations = useLocations(orgId);
  const qc = useQueryClient();
  const location = useFirstLocation(orgId);
  const locId = location.data?.id;
  const base = `/orgs/${orgId}/locations/${locId}`;

  const hours = useQuery({ queryKey: ['hours', locId], queryFn: () => api<{ hours: WeekHours }>(`${base}/hours`), enabled: !!locId });
  const [draft, setDraft] = useState<WeekHours | null>(null);
  useEffect(() => {
    if (hours.data) setDraft(hours.data.hours);
  }, [hours.data]);
  const [saved, setSaved] = useState(false);
  const saveHours = useMutation({
    mutationFn: (h: WeekHours) => api<{ hours: WeekHours }>(`${base}/hours`, { method: 'PUT', body: JSON.stringify({ hours: h }) }),
    onSuccess: (d) => {
      qc.setQueryData(['hours', locId], d);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  const holidays = useQuery({ queryKey: ['holidays', locId], queryFn: () => api<{ holidays: Holiday[] }>(`${base}/holidays`), enabled: !!locId });
  const saveHolidays = useMutation({
    mutationFn: (closed: string[]) => api<{ holidays: Holiday[] }>(`${base}/holidays`, { method: 'PUT', body: JSON.stringify({ closed }) }),
    onSuccess: (d) => qc.setQueryData(['holidays', locId], d),
  });

  const closures = useQuery({ queryKey: ['closures', locId], queryFn: () => api<{ closures: Closure[] }>(`${base}/closures`), enabled: !!locId });
  const addClosure = useMutation({
    mutationFn: (c: { dateFrom: string; dateUntil: string; message?: string }) => api(`${base}/closures`, { method: 'POST', body: JSON.stringify(c) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['closures', locId] }),
  });
  const removeClosure = useMutation({
    mutationFn: (id: string) => api(`/orgs/${orgId}/closures/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['closures', locId] }),
  });

  const dateFmt = new Intl.DateTimeFormat(locale === 'nl' ? 'nl-NL' : 'en-GB', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
  const fmt = (d: string) => dateFmt.format(new Date(`${d}T12:00:00`));

  return (
    <SettingsPage orgId={orgId} aside={<HoursAside orgId={orgId} hours={draft} />}>
      <PageHeader orgId={orgId} title={t('hoursPage.title')} intro={t('hoursPage.intro')} />
      {(locations.data?.length ?? 0) > 1 && <LocationSwitcher orgId={orgId} className="max-w-xs" />}

      <Section title={t('hoursPage.title')}>
        {draft ? <WeekHoursEditor value={draft} onChange={setDraft} closedLabel={t('hours.closed')} /> : <p className="text-muted">{t('common.loading')}</p>}
        <ErrorNote message={errorText(saveHours.error, t('error.generic'))} />
        <div className="flex items-center gap-3">
          <Button onClick={() => draft && saveHours.mutate(draft)} disabled={saveHours.isPending || !draft}>
            {t('common.save')}
          </Button>
          <SavedNote show={saved} />
        </div>
      </Section>

      <Section title={t('hoursPage.holidays')} intro={t('hoursPage.holidaysIntro')}>
        <ul className="flex flex-col divide-y divide-line">
          {holidays.data?.holidays.map((h) => (
            <li key={h.code} className="py-3">
              <Switch
                label={h.name[locale]}
                hint={fmt(h.date)}
                checked={h.closed}
                onChange={(on) => {
                  const closed = holidays.data!.holidays.filter((x) => (x.code === h.code ? on : x.closed)).map((x) => x.code);
                  saveHolidays.mutate(closed);
                }}
              />
            </li>
          ))}
        </ul>
      </Section>

      <Section title={t('hoursPage.closures')} intro={t('hoursPage.closuresIntro')}>
        {closures.data?.closures.length === 0 && <p className="text-muted">{t('hoursPage.noClosures')}</p>}
        <ul className="flex flex-col gap-2">
          {closures.data?.closures.map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-3 rounded-[var(--radius-control)] bg-subtle px-3 py-2.5">
              <div>
                <p className="font-medium">
                  {fmt(c.dateFrom)}
                  {c.dateUntil !== c.dateFrom && ` – ${fmt(c.dateUntil)}`}
                </p>
                {c.message && <p className="text-sm text-muted">“{c.message}”</p>}
              </div>
              <button className="rounded-[var(--radius-control)] px-3 py-2 text-sm text-muted hover:bg-surface" onClick={() => removeClosure.mutate(c.id)}>
                {t('common.delete')}
              </button>
            </li>
          ))}
        </ul>
        <form
          className="grid gap-3 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            const dateFrom = String(f.get('from'));
            const dateUntil = String(f.get('until') || dateFrom);
            addClosure.mutate(
              { dateFrom, dateUntil, message: String(f.get('message') || '') || undefined },
              { onSuccess: () => (e.target as HTMLFormElement).reset() },
            );
          }}
        >
          <Field id="closure-from" name="from" type="date" required label={t('hoursPage.from')} />
          <Field id="closure-until" name="until" type="date" label={t('hoursPage.until')} />
          <div className="sm:col-span-2">
            <Field id="closure-message" name="message" maxLength={160} label={t('hoursPage.message')} placeholder={t('hoursPage.messagePlaceholder')} />
          </div>
          <div className="sm:col-span-2">
            <ErrorNote message={errorText(addClosure.error, t('error.generic'))} />
          </div>
          <Button type="submit" variant="secondary" className="self-start" disabled={addClosure.isPending}>
            {t('common.add')}
          </Button>
        </form>
      </Section>
    </SettingsPage>
  );
}
